import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export const ProgressDialog = ({ open, onOpenChange, workPhases, form, setForm, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Record Progress</DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit}>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Work Phase *</Label>
            <Select
              value={form.work_phase_id}
              onValueChange={(value) => setForm({ ...form, work_phase_id: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select phase" />
              </SelectTrigger>
              <SelectContent>
                {workPhases.map((phase) => (
                  <SelectItem key={phase.id} value={phase.id}>
                    {phase.phase_number} - {phase.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Operator</Label>
              <Input value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Machine</Label>
              <Input value={form.machine} onChange={(e) => setForm({ ...form, machine: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Good Qty *</Label>
              <Input type="number" value={form.good_quantity} onChange={(e) => setForm({ ...form, good_quantity: e.target.value })} required min="0" />
            </div>
            <div className="space-y-2">
              <Label>Scrap Qty</Label>
              <Input type="number" value={form.scrap_quantity} onChange={(e) => setForm({ ...form, scrap_quantity: e.target.value })} min="0" />
            </div>
            <div className="space-y-2">
              <Label>Rework Qty</Label>
              <Input type="number" value={form.rework_quantity} onChange={(e) => setForm({ ...form, rework_quantity: e.target.value })} min="0" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Actual Time (min)</Label>
            <Input type="number" value={form.actual_time_minutes} onChange={(e) => setForm({ ...form, actual_time_minutes: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" className="h-11 px-5" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button type="submit" className="h-11 px-6">Salva Avanzamento</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

export const StatusDialog = ({ open, onOpenChange, newStatus, setNewStatus, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Change Lot Status</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>New Status</Label>
          <Select value={newStatus} onValueChange={setNewStatus}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" className="h-11 px-5" onClick={() => onOpenChange(false)}>Annulla</Button>
        <Button onClick={onSubmit} className="h-11 px-6">Aggiorna Stato</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const DeliveryDialog = ({ open, onOpenChange, lot, form, setForm, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader><DialogTitle>Registra Consegna</DialogTitle></DialogHeader>
      <div className="space-y-4 py-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
          <p className="font-medium text-blue-900">Lotto: {lot?.lot_code}</p>
          <p className="text-blue-700">Ordine: {lot?.order_number || 'N/D'} | Prodotto: {lot?.product_code}</p>
        </div>
        <div className="space-y-2">
          <Label>Data Consegna *</Label>
          <Input type="date" value={form.delivery_date} onChange={e => setForm(f => ({ ...f, delivery_date: e.target.value }))} data-testid="delivery-date" />
        </div>
        <div className="space-y-2">
          <Label>Quantita Consegnata *</Label>
          <Input type="number" min="1" value={form.delivered_quantity} onChange={e => setForm(f => ({ ...f, delivered_quantity: e.target.value }))} placeholder="Quantita" data-testid="delivery-qty" />
        </div>
        <div className="space-y-2">
          <Label>Riferimento DDT</Label>
          <Input value={form.ddt_reference} onChange={e => setForm(f => ({ ...f, ddt_reference: e.target.value }))} placeholder="es. DDT-2026/001" data-testid="delivery-ddt" />
        </div>
        <div className="space-y-2">
          <Label>Note</Label>
          <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Note consegna" data-testid="delivery-notes" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
        <Button onClick={onSubmit} data-testid="delivery-submit">Registra</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const SimplifiedProgressDialog = ({ open, onOpenChange, lot, form, setForm, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader><DialogTitle>Registra Avanzamento Semplificato</DialogTitle></DialogHeader>
      <div className="space-y-4 py-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
          <p className="font-medium text-amber-900">Lotto: {lot?.lot_code}</p>
          <p className="text-amber-700">Ordine: {lot?.order_number || 'N/D'} | Prodotto: {lot?.product_code}</p>
          {lot?.order_line_id && (
            <p className="text-amber-700 mt-1">Le quantita consegnate aggiorneranno automaticamente l'evasione della riga ordine.</p>
          )}
        </div>
        <div className="space-y-2">
          <Label>Data *</Label>
          <Input type="date" value={form.progress_date} onChange={e => setForm(f => ({ ...f, progress_date: e.target.value }))} data-testid="simple-date" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Pz Prodotti</Label>
            <Input type="number" min="0" value={form.produced_quantity} onChange={e => setForm(f => ({ ...f, produced_quantity: e.target.value }))} placeholder="0" data-testid="simple-produced" />
          </div>
          <div className="space-y-2">
            <Label>Pz Consegnati</Label>
            <Input type="number" min="0" value={form.delivered_quantity} onChange={e => setForm(f => ({ ...f, delivered_quantity: e.target.value }))} placeholder="0" data-testid="simple-delivered" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Riferimento DDT</Label>
          <Input value={form.ddt_reference} onChange={e => setForm(f => ({ ...f, ddt_reference: e.target.value }))} placeholder="es. DDT-2026/001" data-testid="simple-ddt" />
        </div>
        <div className="space-y-2">
          <Label>Note</Label>
          <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Note sull'avanzamento" data-testid="simple-notes" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
        <Button onClick={onSubmit} className="bg-amber-600 hover:bg-amber-700" data-testid="simple-submit">Registra</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
